import { useState, useEffect, useCallback, useRef } from 'react';
import type { TimeEntryRefType } from '@jerico/shared';

export interface TimerState {
  matterId: string;
  clientId: string;
  referenceId?: string;
  referenceType?: TimeEntryRefType;
  referenceLabel?: string;
  matterTitle: string;
  startedAt: number;      // epoch ms when timer started
  accumulatedMs: number;   // ms accumulated before current active stretch
  lastActiveAt: number;    // epoch ms of last activity
  paused: boolean;
}

const STORAGE_KEY = 'jerico_active_timer';
const IDLE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

function loadTimer(): TimerState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as TimerState;
  } catch {
    return null;
  }
}

function saveTimer(state: TimerState | null) {
  if (state) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } else {
    localStorage.removeItem(STORAGE_KEY);
  }
}

export function useTimer() {
  const [timer, setTimer] = useState<TimerState | null>(loadTimer);
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  // Persist timer state changes
  useEffect(() => {
    saveTimer(timer);
  }, [timer]);

  // Tick every second to update elapsed display
  useEffect(() => {
    if (timer && !timer.paused) {
      intervalRef.current = setInterval(() => {
        const now = Date.now();
        const activeMs = now - timer.startedAt;
        setElapsed(activeMs);
      }, 1000);
    } else {
      setElapsed(timer?.accumulatedMs ?? 0);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [timer]);

  // Activity tracking — detect idle and auto-pause, listen for resume
  useEffect(() => {
    if (!timer) return;

    function handleActivity() {
      setTimer((prev) => {
        if (!prev) return prev;
        const now = Date.now();

        if (prev.paused) {
          // Resume from pause — don't count idle time
          return {
            ...prev,
            paused: false,
            startedAt: now - prev.accumulatedMs,
            lastActiveAt: now,
          };
        }

        // Check if we were idle
        const idleDuration = now - prev.lastActiveAt;
        if (idleDuration > IDLE_TIMEOUT_MS) {
          // We went idle — accumulate only up to when we went idle, then resume
          const activeUntilIdle = prev.lastActiveAt - prev.startedAt;
          return {
            ...prev,
            accumulatedMs: activeUntilIdle,
            startedAt: now - activeUntilIdle,
            lastActiveAt: now,
          };
        }

        return { ...prev, lastActiveAt: now };
      });
    }

    // Check for idle periodically (only when running, not paused)
    const idleCheck = timer.paused ? undefined : setInterval(() => {
      setTimer((prev) => {
        if (!prev || prev.paused) return prev;
        const now = Date.now();
        if (now - prev.lastActiveAt > IDLE_TIMEOUT_MS) {
          const activeUntilIdle = prev.lastActiveAt - prev.startedAt;
          return { ...prev, paused: true, accumulatedMs: activeUntilIdle };
        }
        return prev;
      });
    }, 30000); // check every 30s

    window.addEventListener('mousemove', handleActivity, { passive: true });
    window.addEventListener('keydown', handleActivity, { passive: true });
    window.addEventListener('click', handleActivity, { passive: true });
    window.addEventListener('scroll', handleActivity, { passive: true });

    return () => {
      if (idleCheck) clearInterval(idleCheck);
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('scroll', handleActivity);
    };
  }, [timer?.paused, !!timer]);

  const startTimer = useCallback(
    (opts: {
      matterId: string;
      clientId: string;
      matterTitle: string;
      referenceId?: string;
      referenceType?: TimeEntryRefType;
      referenceLabel?: string;
    }) => {
      const now = Date.now();
      setTimer({
        ...opts,
        startedAt: now,
        accumulatedMs: 0,
        lastActiveAt: now,
        paused: false,
      });
    },
    [],
  );

  const stopTimer = useCallback((): { durationMinutes: number; state: TimerState } | null => {
    if (!timer) return null;
    const now = Date.now();
    let totalMs: number;
    if (timer.paused) {
      totalMs = timer.accumulatedMs;
    } else {
      // Check if currently idle
      const idleDuration = now - timer.lastActiveAt;
      if (idleDuration > IDLE_TIMEOUT_MS) {
        totalMs = timer.lastActiveAt - timer.startedAt;
      } else {
        totalMs = now - timer.startedAt;
      }
    }
    const durationMinutes = Math.max(1, Math.round(totalMs / 60000));
    const state = { ...timer };
    setTimer(null);
    return { durationMinutes, state };
  }, [timer]);

  const discardTimer = useCallback(() => {
    setTimer(null);
  }, []);

  const getElapsedMinutes = useCallback((): number => {
    if (!timer) return 0;
    const now = Date.now();
    if (timer.paused) return timer.accumulatedMs / 60000;
    const idleDuration = now - timer.lastActiveAt;
    if (idleDuration > IDLE_TIMEOUT_MS) {
      return (timer.lastActiveAt - timer.startedAt) / 60000;
    }
    return (now - timer.startedAt) / 60000;
  }, [timer]);

  return {
    timer,
    elapsed,
    isRunning: !!timer && !timer.paused,
    isPaused: !!timer && timer.paused,
    startTimer,
    stopTimer,
    discardTimer,
    getElapsedMinutes,
  };
}
